import { connect } from '@cerebral/react'
import { Box, Divider, Grid2 as Grid, InputAdornment, Link, Radio, Tooltip, Typography, FormControlLabel, MenuItem } from '@mui/material'
import { Delete } from '@mui/icons-material'
import { Alert, AlertTitle } from '@mui/material'
import { sequences, state } from 'cerebral'
import { FastField, FieldArray, Field } from 'formik'
import { pick, pluck, propEq, sum } from 'ramda'
import { TextField, RadioGroup } from 'formik-mui'
import React, { useCallback, useMemo, useState } from 'react'
import schemaSettings from '../../../../../shared/schemas/resort-schema'
import ValidatedForm from '../../../blocks/ValidatedForm'
import BetweenTimes from '../../../controls/BetweenTimes'
import ConfirmDialog from '../../../controls/ConfirmDialog'
import DebouncedColorPicker from '../../../controls/DebouncedColorPicker'
import LimitedTextField from '../../../controls/LimitedTextField'
import ThemedIconButton from '../../../controls/ThemedIconButton'
import { AddButton } from '../../../elements/AddButton'
import DropUpload from '../../../controls/DropUpload'
import { omitStateFlag } from '../../../../lib/util/cerebral'
import MealIcon from '../../../elements/MealIcon'
import holidayUtils from '../../../../../shared/holiday-regions'
import brand from '../../../../lib/util/brand'

const isQuiltt = brand.name === 'Quiltt'

const { holidayRegions } = holidayUtils

const { eventLabelsSchema, settingsFormSchema } = schemaSettings

const mealDisplayTypeOptions = [
  { label: 'Show only current meal on displays', value: 'true' },
  { label: 'Show current and next two upcoming meals on displays', value: 'false' },
]

const EventSettings = connect(
  {
    organization: state`account.organization`,
    saveSettings: sequences`account.saveSettings`,
    packages: state`account.packages`,
  },
  (props) => ({
    ...props,
    saveSettings: (values) => {
      props.saveSettings({ values: pick(['eventSettings', 'showOnlyOneMeal', 'flagInactiveAfterDays'], values) })
    },
  }),
  ({ organization, saveSettings, packages }) => {
    const eventsEnabled = packages['events']
    const mealsEnabled = packages['menu']
    const [deletingEventType, setDeletingEventType] = useState(null)
    const closeDeletingEventType = useCallback(() => setDeletingEventType(null), [setDeletingEventType])
    const [deletingMealType, setDeletingMealType] = useState(null)
    const closeDeletingMealType = useCallback(() => setDeletingMealType(null), [setDeletingMealType])

    return organization?._id ? (
      <ValidatedForm
        title="Event Settings"
        section="Organization"
        initialValues={organization}
        schema={settingsFormSchema}
        onSubmit={saveSettings}
        validateOnChange={false}
      >
        {({ isSubmitting, setFieldValue, values, errors }) => {
          const { eventSettings: { eventLabels = [], defaultLocationImage = {} } = {}, country } = values
          const percents = pluck('monthTargetPercent', eventLabels).join(',')

          const monthlyDistribution = useMemo(() => {
            const resortEventTypes = eventLabels.filter(propEq('Event', 'category'))
            return { count: resortEventTypes.length, percent: sum(pluck('monthTargetPercent', resortEventTypes)) }
          }, [percents])

          return (
            <Box flex={1}>
              <Grid container mb={4} spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Field name="eventSettings.holidayRegion" component={TextField} label="Show Holidays" select>
                    {holidayRegions
                      .filter(({ value }) => value === 'none' || value === country)
                      .map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                  </Field>
                </Grid>
                {isQuiltt && eventsEnabled ? (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Field required label="Inactivity Threshold (Days)" name="flagInactiveAfterDays" component={TextField} type="number" />
                  </Grid>
                ) : null}
              </Grid>

              {eventsEnabled ? (
                <>
                  <Typography variant="h3" gutterBottom>
                    Event Types
                  </Typography>

                  {monthlyDistribution?.count > 0 && monthlyDistribution?.percent !== 100 && (
                    <Box my={2}>
                      <Alert severity="error">
                        <AlertTitle>Invalid distribution.</AlertTitle>
                        Please ensure that the combined percentage for all event types adds up to <strong>100%</strong>. Currently, the total is{' '}
                        {monthlyDistribution?.percent}%.
                      </Alert>
                    </Box>
                  )}

                  <FieldArray
                    name="eventSettings.eventLabels"
                    render={(arrayHelpers) => {
                      const eventTypeLabel = eventLabels[deletingEventType]?.label
                      return (
                        <>
                          <ConfirmDialog
                            open={deletingEventType !== null}
                            title="Delete event type"
                            onConfirm={() => arrayHelpers.remove(deletingEventType)}
                            onClose={closeDeletingEventType}
                            confirmText="Delete"
                          >
                            Are you sure you want to delete {eventTypeLabel ? <strong>{eventTypeLabel}</strong> : 'this event type'}? If you delete this event
                            type, any past events that it is associated with it will no longer have a type.
                          </ConfirmDialog>

                          {eventLabels.map((label, index) => {
                            if (label.category !== 'Event') {
                              return null
                            }

                            return (
                              <Box key={label._id || `${label.name}:${index}`}>
                                <Grid container spacing={2} alignItems="center">
                                  <Grid size={{ xs: 11 }} container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                      <FastField
                                        label="Label"
                                        name={`eventSettings.eventLabels.${index}.label`}
                                        component={LimitedTextField}
                                        schema={settingsFormSchema}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                      <FastField label="Color" name={`eventSettings.eventLabels.${index}.color`} component={DebouncedColorPicker} />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                      <Tooltip title="Minimum number of events of this type per week">
                                        <div>
                                          <FastField
                                            label="# of events"
                                            name={`eventSettings.eventLabels.${index}.minEventsPerWeek`}
                                            component={TextField}
                                            schema={settingsFormSchema}
                                            type="number"
                                          />
                                        </div>
                                      </Tooltip>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                      <Tooltip title="Monthly target percent (%) of activities">
                                        <div>
                                          <FastField
                                            schema={settingsFormSchema}
                                            label="% of activities"
                                            name={`eventSettings.eventLabels.${index}.monthTargetPercent`}
                                            component={TextField}
                                            type="number"
                                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                          />
                                        </div>
                                      </Tooltip>
                                    </Grid>
                                  </Grid>
                                  <Grid size={{ xs: 1 }}>
                                    <Box p={1}>
                                      <ThemedIconButton color="danger" onClick={() => setDeletingEventType(index)}>
                                        <Delete />
                                      </ThemedIconButton>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            )
                          })}

                          <Box mt={1} mb={5} display="flex" justifyContent="flex-start">
                            <AddButton disabled={isSubmitting} onClick={() => arrayHelpers.push(eventLabelsSchema.cast())} title="Add an event type" />
                          </Box>
                        </>
                      )
                    }}
                  />
                </>
              ) : null}

              {mealsEnabled ? (
                <Box mt={4}>
                  <Typography variant="h3" gutterBottom component={Link} href="#meal-types" id="meal-types" sx={{ textDecoration: 'none', color: 'inherit' }}>
                    Meal Types
                  </Typography>

                  <FieldArray
                    name="eventSettings.eventLabels"
                    render={(arrayHelpers) => {
                      const mealTypeLabel = eventLabels[deletingMealType]?.label
                      return (
                        <>
                          <ConfirmDialog
                            open={deletingMealType !== null}
                            title="Delete meal type"
                            onConfirm={() => arrayHelpers.remove(deletingMealType)}
                            onClose={closeDeletingMealType}
                            confirmText="Delete"
                          >
                            Are you sure you want to delete {mealTypeLabel ? <strong>{mealTypeLabel}</strong> : 'this meal type'}? If you delete this meal type,
                            any past meals that it is associated with it will no longer have a type.
                          </ConfirmDialog>

                          {eventLabels.map((label, index) => {
                            if (label.category !== 'Menu') {
                              return null
                            }
                            return (
                              <Box key={index}>
                                <Grid container spacing={2} alignItems="center">
                                  <Grid size={{ xs: 11 }} container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                      <FastField
                                        label="Label"
                                        name={`eventSettings.eventLabels.${index}.label`}
                                        component={LimitedTextField}
                                        schema={settingsFormSchema}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                      <FastField label="Color" name={`eventSettings.eventLabels.${index}.color`} component={DebouncedColorPicker} />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                      <BetweenTimes
                                        required
                                        startLabel="Start Time"
                                        startName={`eventSettings.eventLabels.${index}.start`}
                                        endLabel="End Time"
                                        endName={`eventSettings.eventLabels.${index}.end`}
                                      />
                                    </Grid>
                                  </Grid>
                                  <Grid size={{ xs: 1, sm: 1 }}>
                                    <Box p={1}>
                                      <ThemedIconButton color="danger" onClick={() => setDeletingMealType(index)}>
                                        <Delete />
                                      </ThemedIconButton>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            )
                          })}

                          <Box mt={1} display="flex" justifyContent="flex-start">
                            <AddButton
                              disabled={isSubmitting}
                              onClick={() => arrayHelpers.push({ ...eventLabelsSchema.cast(), category: 'Menu' })}
                              title="Add a meal type"
                            />
                          </Box>
                        </>
                      )
                    }}
                  />
                  <Divider sx={{ my: 4 }} />

                  <Box mb={4}>
                    <Typography variant="h5" mb={2}>
                      Signage Header Image for Meals
                    </Typography>
                    <Box maxWidth={400}>
                      <DropUpload
                        optional
                        ratio={9 / 16}
                        image={omitStateFlag(defaultLocationImage)}
                        onChange={(image) => setFieldValue('eventSettings.defaultLocationImage', image)}
                        error={errors.image}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="h5" mb={2}>
                      Meal Display
                    </Typography>
                    <Box display="flex" alignItems="center" gap={4}>
                      <Field name="showOnlyOneMeal" component={RadioGroup}>
                        {mealDisplayTypeOptions.map(({ label, value }) => (
                          <FormControlLabel key={value} value={value} control={<Radio />} label={label} />
                        ))}
                      </Field>
                      <MealIcon value={values.showOnlyOneMeal} />
                    </Box>
                  </Box>
                </Box>
              ) : null}
            </Box>
          )
        }}
      </ValidatedForm>
    ) : null
  }
)

export default EventSettings
